import React, { useRef, useState } from 'react';
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Row,
    Col,
    Alert,
    Spinner,
    Badge
} from "reactstrap";
import styles from './riskMassive.module.scss';

const ModalRiskMassive = ({ open, onClose, onDownloadTemplate, onUpload, onConfirmChanges }) => {
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [validations, setValidations] = useState([]);
    const [showValidations, setShowValidations] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [isProcessing, setIsProcessing] = useState(false);

    const handleDownload = () => {
        if (onDownloadTemplate) onDownloadTemplate();
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (file && onUpload) {
            setIsUploading(true);
            setUploadError(null);
            setUploadSuccess(false);
            
            const reader = new FileReader();

            reader.onloadend = async () => {
                try {
                    const base64Data = reader.result.split(',')[1];
                    const result = await onUpload({ blob: base64Data });
                    if (result && result.response && result.response.error) {
                        setUploadError(result.response.msgError || 'An error occurred during upload');
                        setShowValidations(false);
                        setValidations([]);
                    } else if (result && result.response && result.response.validations) {
                        setValidations(result.response.validations);
                        setShowValidations(true);
                        setUploadSuccess(true);
                        setPage(1);
                    } else {
                        setUploadSuccess(true);
                        setShowValidations(false);
                        // Auto-close modal after success if no validations
                        setTimeout(() => {
                            handleClose();
                        }, 2000);
                    }
                } catch (error) {
                    setUploadError(error.message || 'An unexpected error occurred');
                    setShowValidations(false);
                    setValidations([]);
                } finally {
                    setIsUploading(false);
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                }
            };
            
            reader.readAsDataURL(file);
        }
    };

    const handleClose = () => {
        setIsUploading(false);
        setUploadError(null);
        setUploadSuccess(false);
    setValidations([]);
        setShowValidations(false);
    setPage(1);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
    };

    const handleConfirm = async () => {
        setIsProcessing(true);
        if (onConfirmChanges) {
            try {
                await onConfirmChanges({data: validations});
                setIsProcessing(false);
                handleClose();
            } catch (error) {
                setIsProcessing(false);
                setUploadError('Failed to confirm changes: ' + error.message);
            }
        }
    };

    const handleReject = () => {
        setShowValidations(false);
        setValidations([]);
        setUploadSuccess(false);
        setPage(1);
    };

    const renderValidationItem = (validation, index) => {
        const hasFromValue = (value) => value && value !== "0" && value !== "";
        const Cell = ({ from, to }) => (
            <td className={styles.diffCell}>
                {hasFromValue(from) ? (
                    <span className={styles.oldValue}>{from}</span>
                ) : (
                    <span className={styles.noValue}>No previous value</span>
                )}
                <span className={styles.arrow}><i className="fa fa-arrow-down" /></span>
                <span className={styles.newValue}>{to || 'No change'}</span>
            </td>
        );

        return (
            <tr key={index}>
                <td>
                    <strong>{validation.issuerName}</strong>
                    <br />
                    <small className="text-muted">ID: {validation.issuerId}</small>
                </td>
                <td className="text-center">
                    <Badge color="info">{validation.AssetsToModify}</Badge>
                </td>
                <Cell from={validation.IssuerRiskDescFrom} to={validation.IssuerRiskDescTo} />
                <Cell from={validation.RatingBipyFromName} to={validation.RatingBipyToName} />
                <Cell from={validation.RatingScoreFrom} to={validation.RatingScoreTo} />
            </tr>
        );
    };

    const totalPages = Math.max(1, Math.ceil(validations.length / pageSize));
    if (page > totalPages) setPage(totalPages);

    const paginatedValidations = showValidations
        ? validations.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)
        : [];

    const handleExportCsv = () => {
        if (!validations || !validations.length) return;
        const headers = [
            'IssuerId','IssuerName','AssetsToModify','IssuerRiskDescFrom','IssuerRiskDescTo','RatingBipyFromName','RatingBipyToName','RatingScoreFrom','RatingScoreTo'
        ];
        const escape = (v) => {
            if (v === undefined || v === null) return '';
            const s = String(v).replace(/"/g,'""');
            return '"' + s + '"';
        };
        const lines = [headers.join(',')];
        validations.forEach(v => {
            lines.push([
                v.issuerId,
                v.issuerName,
                v.AssetsToModify,
                v.IssuerRiskDescFrom || '',
                v.IssuerRiskDescTo || '',
                v.RatingBipyFromName || '',
                v.RatingBipyToName || '',
                v.RatingScoreFrom || '',
                v.RatingScoreTo || ''
            ].map(escape).join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'risk_massive_validations.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const goToPage = (p) => {
        if (p < 1 || p > totalPages) return; setPage(p);
    };

    return (
    <Modal isOpen={open} toggle={handleClose} size={showValidations ? "xl" : "md"} className={styles.riskModal}>
            <ModalHeader toggle={handleClose}>
                <i className="fa fa-upload mr-2"></i>
                {showValidations ? 'Review Changes - Massive Risk Load' : 'Massive Risk Load'}
            </ModalHeader>
            <ModalBody>
                {!showValidations && (
                    <Alert color="info" className="mb-3">
                        <i className="fa fa-info-circle mr-2"></i>
                        Download the template, fill it with your data, and upload it here.
                    </Alert>
                )}

                {uploadError && (
                    <Alert color="danger" className="mb-3">
                        <i className="fa fa-exclamation-triangle mr-2"></i>
                        <strong>Upload Error:</strong> {uploadError}
                    </Alert>
                )}

                {uploadSuccess && !showValidations && (
                    <Alert color="success" className="mb-3">
                        <i className="fa fa-check-circle mr-2"></i>
                        <strong>Success!</strong> Risk data has been uploaded successfully.
                    </Alert>
                )}

                {showValidations && (
                    <div>
                        <Alert color="warning" className="mb-3">
                            <i className="fa fa-exclamation-circle mr-2"></i>
                            <strong>Review Changes:</strong> The following changes will be applied to your assets. Please review and confirm.
                        </Alert>
                        <div className={styles.summaryBox}>
                            <div className={styles.totalAssets}>
                                {validations.reduce((total, v) => total + parseInt(v.AssetsToModify), 0)} total assets affected
                            </div>
                            <div style={{display:'flex',gap:8,alignItems:'center'}}>
                                <button type="button" className={styles.exportBtn} onClick={handleExportCsv}>
                                    <i className="fa fa-file-excel-o" /> Export CSV
                                </button>
                                <small className="text-muted">Issuers: {validations.length}</small>
                            </div>
                        </div>
                        <div className={styles.tableWrapper}>
                            {isUploading && (
                                <div className={styles.loadingOverlay}>
                                    <Spinner size="sm" className="mr-2" /> Processing...
                                </div>
                            )}
                            <table className={styles.tableValidations}>
                                <thead>
                                    <tr>
                                        <th style={{minWidth:170}}>Issuer</th>
                                        <th style={{width:90}} className="text-center">Assets</th>
                                        <th style={{minWidth:160}}>Risk Description</th>
                                        <th style={{minWidth:140}}>Rating Agency</th>
                                        <th style={{minWidth:130}}>Rating Score</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedValidations.map((validation, index) => renderValidationItem(validation, index))}
                                </tbody>
                            </table>
                            <div className={styles.paginationBar + ' ' + styles.paginationSticky}>
                                <div className="pages">
                                    <button className="pageBtn" title="First" disabled={page===1} onClick={() => goToPage(1)}>
                                        <i className="fa fa-angle-double-left iconOnly" />
                                    </button>
                                    <button className="pageBtn" title="Previous" disabled={page===1} onClick={() => goToPage(page-1)}>
                                        <i className="fa fa-angle-left iconOnly" />
                                    </button>
                                    <span style={{margin:'0 10px', fontWeight:500, fontSize:12}}>
                                        {page} / {totalPages}
                                    </span>
                                    <button className="pageBtn" title="Next" disabled={page===totalPages} onClick={() => goToPage(page+1)}>
                                        <i className="fa fa-angle-right iconOnly" />
                                    </button>
                                    <button className="pageBtn" title="Last" disabled={page===totalPages} onClick={() => goToPage(totalPages)}>
                                        <i className="fa fa-angle-double-right iconOnly" />
                                    </button>
                                </div>
                                <div style={{display:'flex',alignItems:'center',gap:6}}>
                                    <span style={{fontSize:12}}>Rows:</span>
                                    <select value={pageSize} onChange={(e)=>{setPageSize(parseInt(e.target.value)); setPage(1);}}>
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!showValidations && (
                    <Row>
                        <Col xs={12} className="mb-3">
                            <h6>Step 1: Download Template</h6>
                            <Button
                                color="secondary"
                                outline
                                onClick={handleDownload}
                                className="w-100"
                                disabled={isUploading}
                            >
                                <i className="fa fa-download mr-2"></i>
                                Download Template
                            </Button>
                        </Col>

                        <Col xs={12}>
                            <h6>Step 2: Upload Completed File</h6>
                            <input
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleUpload}
                                disabled={isUploading}
                            />
                            <Button
                                color="primary"
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                className="w-100"
                                disabled={isUploading || uploadSuccess}
                            >
                                {isUploading ? (
                                    <span>
                                        <Spinner size="sm" className="mr-2" />
                                        Uploading...
                                    </span>
                                ) : (
                                    <span>
                                        <i className="fa fa-upload mr-2"></i>
                                        Upload File
                                    </span>
                                )}
                            </Button>
                        </Col>
                    </Row>
                )}
            </ModalBody>
            <ModalFooter>
                {showValidations ? (
                    <div className={styles.actionsBar}>
                        <Button color="secondary" onClick={handleReject} disabled={isUploading}>
                            <i className="fa fa-arrow-left mr-2" /> Back
                        </Button>
                        <Button color="danger" onClick={handleClose} disabled={isUploading}>
                            <i className="fa fa-times mr-2" /> Cancel
                        </Button>
                        <Button color="success" onClick={handleConfirm} disabled={isUploading || isProcessing}>
                            {isProcessing ? (
                                <span>
                                    <Spinner size="sm" className="mr-2" />
                                    Confirming...
                                </span>
                            ) : (
                                <span>
                                    <i className="fa fa-check mr-2" /> Confirm & Apply
                                </span>
                            )}
                        </Button>
                    </div>
                ) : (
                    <div className={styles.actionsBar}>
                        <Button color="secondary" onClick={handleClose} disabled={isUploading}>
                            Cancel
                        </Button>
                    </div>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default ModalRiskMassive;